/**
 * VaultLogo — the JobVault brand mark.
 *
 * "Banded vault" (C2) in cobalt + soft-blue: a small rectangular grip on top,
 * a thick stroked rounded body, two cobalt-gradient bands at top and bottom,
 * a soft pale-cobalt middle band, and a small cobalt dial in the centre.
 *
 * Props
 *   size  — square render in pixels (default 28)
 *   tone  — "colour" (default) | "mono" (renders in `currentColor`)
 *   dark  — true when sitting on a dark background; flips strokes/body so the
 *           glyph stays legible against ink. Cobalt bands remain cobalt.
 */
export default function VaultLogo({ size = 28, tone = "colour", dark = false, className = "", style, ...rest }) {
  const mono = tone === "mono";
  const ink = "currentColor";
  const stroke = mono ? ink : dark ? "#FFFFFF" : "#0A0A0A";
  const bodyFill = dark ? "#1a1a1a" : "#FFFFFF";
  const dialFill = dark ? "#1a1a1a" : "#FFFFFF";
  const dialDot  = mono ? ink : "#1B41F8";

  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      style={style}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...rest}
    >
      <defs>
        <linearGradient id={`vl-band-${dark ? "d" : "l"}-${mono ? "m" : "c"}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={mono ? ink : "#4A6BFF"} stopOpacity={mono ? 0.22 : 1}/>
          <stop offset="100%" stopColor={mono ? ink : "#1B41F8"} stopOpacity={mono ? 0.38 : 1}/>
        </linearGradient>
        <linearGradient id={`vl-mid-${dark ? "d" : "l"}-${mono ? "m" : "c"}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={mono ? ink : "#EAEEFF"} stopOpacity={mono ? 0.08 : 1}/>
          <stop offset="100%" stopColor={mono ? ink : "#D0D8FF"} stopOpacity={mono ? 0.16 : 1}/>
        </linearGradient>
      </defs>

      {/* Grip handle on top */}
      <rect x="40" y="11" width="20" height="11" rx="3"
        fill="none" stroke={stroke} strokeWidth="3.5" strokeLinejoin="round"/>

      {/* Vault body */}
      <rect x="14" y="22" width="72" height="68" rx="5"
        fill={bodyFill} stroke={stroke} strokeWidth="4" strokeLinejoin="round"/>

      {/* Top band */}
      <rect x="20" y="28" width="60" height="10" rx="1.5"
        fill={`url(#vl-band-${dark ? "d" : "l"}-${mono ? "m" : "c"})`} stroke={stroke} strokeWidth="2.5"/>

      {/* Bottom band */}
      <rect x="20" y="74" width="60" height="10" rx="1.5"
        fill={`url(#vl-band-${dark ? "d" : "l"}-${mono ? "m" : "c"})`} stroke={stroke} strokeWidth="2.5"/>

      {/* Middle door */}
      <rect x="20" y="42" width="60" height="28" rx="2"
        fill={`url(#vl-mid-${dark ? "d" : "l"}-${mono ? "m" : "c"})`} stroke={stroke} strokeWidth="2.5"/>

      {/* Central dial */}
      <circle cx="50" cy="56" r="7" fill={dialFill} stroke={stroke} strokeWidth="2.5"/>
      <circle cx="50" cy="56" r="2" fill={dialDot}/>
    </svg>
  );
}
